import { makeStyles } from '@material-ui/core/styles'
import createStyles from '@material-ui/core/styles/createStyles'
import { ClassNameMap } from '@material-ui/core/styles/withStyles'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import i18n from 'i18next'
import upperFirst from 'lodash/upperFirst'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineFileExcel } from 'react-icons/ai'
import { TableCellProps } from 'react-virtualized'

import api from 'src/api'
import { PolicyAmount, PolicyAmount_StatusEnum } from 'src/api/gen/api'
import { CurrencyKind } from 'src/api/types/units'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import { CellLink } from 'src/components/Table/Cells'
import ExportableTable from 'src/components/Table/Templates/ExportableTable'
import { enumCellRenderer } from 'src/components/Table/utils'
import { IColumnProps } from 'src/components/Table/VirtualizedTable'
import { useCurrencyFormatter } from 'src/layout/AgencySettings'

import { IPenaltyScheme } from '.'

interface IProps {
  className?: string
  data: PolicyAmount[]
  penaltyScheme: IPenaltyScheme
  providerId: string
  totalCount: number
}

const PenaltySchemesTable = function (props: IProps) {
  const { t } = useTranslation()
  const currencyFormatter = useCurrencyFormatter(CurrencyKind.Global)
  const classes = useStyles()

  const columns: IColumnProps[] = [
    {
      label: upperFirst(t('titles.policy_name')),
      width: 200,
      dataKey: 'policy_name',
      cellRenderer: (cellProps: TableCellProps) => {
        const policyAmount = cellProps.rowData
        const url = `/policy/${policyAmount.policy_id}`
        return (
          <CellLink url={url} tooltip sx={{ alignSelf: 'center' }}>
            {policyAmount.policy_name}
          </CellLink>
        )
      },
    },
    {
      label: upperFirst(t('titles.penalty_scheme.status')),
      width: 200,
      dataKey: 'status',
      cellRenderer: (cellProps: TableCellProps) => (
        <Typography sx={{ alignSelf: 'center' }}>
          {enumCellRenderer(cellProps, (status: PolicyAmount_StatusEnum) =>
            upperFirst(t(enumsTranslationKeys.PenaltySchemesStatus[status]))
          )}
        </Typography>
      ),
    },
    {
      label: upperFirst(t('titles.number_of_penalties')),
      width: 200,
      dataKey: 'offenses_count',
      cellRenderer: (cellProps: TableCellProps) => (
        <Typography sx={{ alignSelf: 'center' }}>{cellProps.cellData?.toFixed(0)}</Typography>
      ),
    },
    {
      label: upperFirst(t('titles.amount')),
      width: 200,
      dataKey: 'amount',
      cellRenderer: (cellProps: TableCellProps) => (
        <Typography sx={{ alignSelf: 'center' }}>{currencyFormatter(cellProps.cellData?.toFixed(2))}</Typography>
      ),
    },
    {
      label: '',
      width: 50,
      dataKey: 'export_icon',
      cellRenderer: (cellProps: TableCellProps) => cellWithAmountAndExportIcon(classes, cellProps.rowData.policy_id),
    },
  ]

  const onExportClick = async function (policy_id: string) {
    await api.offenses.exportPolicyOffenseListNotFilteredByStartDate({
      include_abnormals: false,
      policy_id: policy_id,
      provider_ids: [props.providerId],
      start_date_after: props.penaltyScheme.start_date as unknown as string,
      start_date_before: props.penaltyScheme.end_date as unknown as string,
      lang: i18n.language,
    })
  }

  const cellWithAmountAndExportIcon = function (classes: ClassNameMap, policy_id: string) {
    return (
      <Box>
        <Tooltip title={t('common.export') as string} placement="bottom" classes={{ tooltip: classes.tooltip }}>
          <IconButton sx={{ fontSize: 25, color: '#1d6f42' }} onClick={() => onExportClick(policy_id)}>
            <AiOutlineFileExcel />
          </IconButton>
        </Tooltip>
      </Box>
    )
  }

  return (
    <ExportableTable
      data={props.data}
      totalCount={props.totalCount}
      columns={columns}
      className={props.className}
      setIsOverDimension={function (_: boolean) {
        // nothing to see here
      }}
    />
  )
}

const useStyles = makeStyles(() => {
  return createStyles({
    exportIcon: {
      fontSize: 30,
      cursor: 'pointer',
      color: '#192233',
    },
  })
})

export default PenaltySchemesTable
